import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "..";
import { SEARCH_DELAY } from "../../constant";

/**
 * Load all managers and make it possible to manage them
 */
class ManagerStore {
    private sdk: ApplicationSdk;

    public managerListLoading: boolean = false;
    public managerList: Array<any> = [];
    public sortOrder: number = 1;
    public sortField: string = 'id';
    public searchValue: string = '';

    public currentPage: number = 1;
    public maxPage: number = 1;
    public totalCount: number = 0;
    public latestPage: number = 1;

    public managerDeleteConfirmation: any = null;

    public timeout: any = null;

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            managerListLoading: observable,
            managerList: observable,
            sortOrder: observable,
            sortField: observable,
            searchValue: observable,
            currentPage: observable,
            maxPage: observable,
            totalCount: observable,
            fetchManagerList: action,
            onSort: action,
            updateSearch: action,
            clearSearch: action,
            onSearch: action,
            onDialogLeave: action,
            managerDeleteConfirmation: observable,
            onDeleteManager: action,
            onDeleteManagerCancel: action,
            onDeleteManagerConfirm: action,
            allManagers: computed
        });

        this.sdk = sdk;

        // Load state from localStorage
        this.loadFromLocalStorage();

    }


    private saveToLocalStorage(): void {
        const state = {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue
        };
        localStorage.setItem('manager', JSON.stringify(state));
    }

    private loadFromLocalStorage(): void {
        const savedState = localStorage.getItem('manager');
        if (savedState) {
            try {
                const { sortOrder, sortField, searchValue } = JSON.parse(savedState);
                runInAction(() => {
                    this.sortOrder = sortOrder || 1;
                    this.sortField = sortField || 'id';
                    this.searchValue = searchValue || '';
                });
            } catch (error) {
                console.error('Failed to load state from localStorage:', error);
            }
        }
    }

    public async init(): Promise<void> {
        this.fetchManagerList();
    }

    public async onSearch(): Promise<void> {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        await this.fetchManagerList();
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
            this.fetchManagerList();
        }
    }

    public updateSearch(value: string): void {
        this.searchValue = value;

        // Save updated state to localStorage
        this.saveToLocalStorage();

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (SEARCH_DELAY) {
            this.timeout = setTimeout(() => {
                this.fetchManagerList();
            }, SEARCH_DELAY);
        }
    }

    public onPageChange(nextPage: number): void {
        this.fetchManagerList(nextPage);
    }

    public async onSort(sortField: string): Promise<void> {
        runInAction(() => {
            if (this.sortField === sortField) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortOrder = -1;
            }
            this.sortField = sortField;

            // Save updated state to localStorage
            this.saveToLocalStorage();
        });

        await this.fetchManagerList();
    }

    public async fetchManagerList(page: number = 1): Promise<void> {
        this.latestPage = page;
        runInAction(() => {
            this.managerListLoading = true;
        });

        const managerListResult: any = await this.sdk.fetchApi(
            'manager',
            'query',
            {
                sortvalue: this.sortField,
                sort: this.sortOrder === 1 ? 'asc' : 'desc',
                search: this.searchValue || '',
                page
            });

        runInAction(() => {
            this.managerListLoading = false;

            if (managerListResult?.status === 200) {
                this.managerList = managerListResult.json.data;
                this.currentPage = managerListResult.json.pagination.page;
                this.totalCount = managerListResult.json.pagination.total;
                this.maxPage = Math.ceil(this.totalCount / managerListResult.json.pagination.pageSize);
            }
        });
    }


    public onDeleteManager(manager: any): void {
        this.managerDeleteConfirmation = manager;
    }

    public onDeleteManagerCancel(): void {
        this.managerDeleteConfirmation = null;
    }

    public async onDeleteManagerConfirm(): Promise<void> {
        if (this.managerDeleteConfirmation?.id) {
            const result = await this.sdk.fetchApi(
                'manager',
                'delete',
                {
                    manager_id: this.managerDeleteConfirmation.id
                });

                if(result?.status === 200) {
                    this.sdk.showSuccessToast('Manager erfolgreich gelöscht');
                    await this.fetchManagerList();
                }
        }
        this.managerDeleteConfirmation = null;
    }


    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'manager' && event?.event === 'SAVED') {
            await this.fetchManagerList(this.latestPage);
        }
    }

    get allManagers(): Array<any> | null {
        if (!this.managerList) return null;
        return this.managerList.filter((item: any) => {
            return true;
            // return item.name.toLowerCase().includes(this.productQuickSearch.toLowerCase()) || item?.description?.toLowerCase()?.includes(this.productQuickSearch.toLowerCase());
        });
    }

    public onLeaveStore(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
}

export default ManagerStore;